/**
 * usePrincipalProfile — fetches the full profile for an IdP principal
 * (devices, agents, recent alerts) to populate the OwnerDrawer.
 *
 * P2 endpoint. Returns `null` on 404 so the drawer can render the
 * documented empty state instead of an error banner.
 */
import { useQuery } from "@tanstack/react-query";
import { lighthouseGet, LighthouseApiError } from "@/services/lighthouseApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type { PrincipalProfileDTO } from "@/types/lighthouseApi";

export function usePrincipalProfile(
  principalId: string | undefined | null,
  options: { enabled?: boolean } = {},
) {
  const connectorOn = useConnectorEnabled("lighthouse");
  const id = (principalId ?? "").trim();
  const enabled = connectorOn && id.length > 0 && options.enabled !== false;

  return useQuery({
    enabled,
    queryKey: ["lighthouse", "principals", "profile", id],
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    queryFn: async (): Promise<PrincipalProfileDTO | null> => {
      try {
        const { data } = await lighthouseGet<PrincipalProfileDTO>(
          `/api/v1/lighthouse/principals/${encodeURIComponent(id)}/profile`,
        );
        return data ?? null;
      } catch (err) {
        if (err instanceof LighthouseApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
  });
}
